import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Trophy, Search } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import SearchDialog from "./SearchDialog";
import { useI18n } from "@/i18n/I18nContext";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { t } = useI18n();

  const NAV = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/matches", label: t("nav.matches"), key: "matches" },
    { to: "/teams", label: t("nav.teams"), key: "teams" },
    { to: "/predictions", label: t("nav.predictions"), key: "predictions" },
    { to: "/standings", label: t("nav.standings"), key: "standings" },
  ];

  // ⌘K / Ctrl+K shortcut to open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 border-b border-white/10 glass"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-8 h-8 bg-volt flex items-center justify-center">
              <Trophy className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl tracking-tighter group-hover:text-volt transition-colors">
              GOAL<span className="text-volt">GRID</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                    isActive ? "text-volt" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <button
              data-testid="search-open-btn"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-white/40 text-xs uppercase tracking-[0.18em] text-white/70 transition-colors"
              aria-label={t("search.open")}
            >
              <Search className="w-3.5 h-3.5" />
              <span>{t("search.open")}</span>
              <kbd className="ml-2 px-1.5 py-0.5 text-[10px] font-mono border border-white/15 text-white/50">
                ⌘K
              </kbd>
            </button>
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white/15">
              <span className="live-dot" />
              <span className="text-xs uppercase tracking-[0.2em] text-white/80">{t("nav.live_now")}</span>
            </div>
            <LanguageSwitcher />
          </div>

          <div className="md:hidden flex items-center gap-2">
            <button
              data-testid="search-open-btn-mobile"
              onClick={() => setSearchOpen(true)}
              className="text-white p-1"
              aria-label={t("search.open")}
            >
              <Search className="w-5 h-5" />
            </button>
            <LanguageSwitcher />
            <button
              data-testid="mobile-menu-toggle"
              className="text-white"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-white/10 bg-[#0A0A0A]">
            <div className="px-4 py-3 flex flex-col">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${item.key}`}
                  className={`py-3 text-sm uppercase tracking-[0.18em] ${
                    location.pathname === item.to ? "text-volt" : "text-white/70"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
